/**
 * JobRowCard — shared shell for To-Do / In-Progress / Paused job cards
 * (extracted Feb 2026 from JobsEnhanced.jsx).
 *
 * What it owns:
 *   - The card frame with status-coloured left border
 *   - Bulk-select checkbox (top-right)
 *   - Header row: job number chip, title (click to open detail), status badge,
 *     overdue/schedule-overdue chips, label chips, description, unread-notes badge
 *   - Details grid: customer, location, team/assignee, on-behalf audit,
 *     time-since-assignment, scheduled start, scheduled completion
 *   - A `<div className="pt-3 border-t">…</div>` action slot rendered from
 *     `children` so each status owns its own button arrangement (Start vs
 *     Pause+Complete vs Resume+Complete) without runtime branching.
 *
 * Why this shape:
 *   The shell is ~80 lines of nearly-identical JSX repeated 3 times in the
 *   parent (270+ LOC of duplication). The action footer diverges per status
 *   and per role (worker vs admin/supervisor), so keeping it inline in the
 *   parent makes those differences explicit and grep-able.
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import LabelChip from '../LabelChip';
import { TeamViaBadge } from './TeamViaBadge';
import JobAssignees from './JobAssignees';
import {
  User as UserIcon, Users, MapPin, Clock, UserCog, QrCode,
} from 'lucide-react';

const VARIANT_BORDER = {
  todo:        'border-[#1E88E5]',
  inprogress:  'border-[#FFA000]',
  paused:      'border-purple-600',
};
const VARIANT_OVERDUE = 'border-[#EF4444]'; // todo can flip to overdue red

export default function JobRowCard({
  job,
  variant,                       // 'todo' | 'inprogress' | 'paused'
  // Display helpers from the parent (memoized there already)
  labelsById,
  getUserName,
  getStatusBadge,
  // Time / status helpers
  isOverdue = false,
  isJobScheduleOverdue = false,
  timeSince = null,
  // Roles + selection
  canManageJobs,
  isSelected,
  onToggleSelect,                // (jobId) => void
  onOpenDetail,                  // (job) => void
  unreadNotes = 0,
  users = [],                    // workers for the inline crew editor (Sep 2026)
  onCrewChanged,                 // () => void — refetch jobs after a crew change
  // Per-status action footer comes through children — each status renders
  // its own arrangement inline in the parent, keeping divergence explicit.
  children,
  testIdPrefix,                  // 'todo' | 'inprogress' | 'paused' — used in selector ids
}) {
  const borderClass = isOverdue ? VARIANT_OVERDUE : VARIANT_BORDER[variant] || 'border-slate-600';

  return (
    <Card
      className={`relative shadow-md border-l-4 ${borderClass} bg-[#1A2433]`}
      data-testid={`${testIdPrefix || variant}-card-${job.id}`}
    >
      {canManageJobs && (
        <input
          type="checkbox"
          aria-label={`Select job ${job.job_number || job.title}`}
          checked={!!isSelected}
          onChange={() => onToggleSelect?.(job.id)}
          className="absolute top-3 right-3 w-4 h-4 cursor-pointer accent-blue-500 z-10"
          data-testid={`select-job-${job.id}`}
          title="Select for bulk action"
        />
      )}
      <CardContent className="p-5">
        <div className="space-y-3">
          {/* Header — job number chip, title (click to open detail), badges, labels, desc */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <h3 className="text-lg font-bold text-white flex items-baseline gap-2 flex-wrap">
                  {job.job_number && (
                    <span
                      className="text-xs font-mono text-[#1E88E5] bg-[#1E88E5]/10 px-2 py-0.5 rounded"
                      data-testid={`job-number-${job.id}`}
                    >
                      {job.job_number}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenDetail?.(job); }}
                    className="text-left hover:text-blue-400 hover:underline transition-colors cursor-pointer"
                    data-testid={`open-job-detail-${job.id}`}
                    title="Open job details (notes, files, activity)"
                  >
                    {job.title}
                  </button>
                  {unreadNotes > 0 && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white animate-pulse"
                      title={`${unreadNotes} unread note${unreadNotes === 1 ? '' : 's'}`}
                      data-testid={`unread-notes-badge-${job.id}`}
                    >
                      {unreadNotes} new
                    </span>
                  )}
                </h3>
                <TeamViaBadge job={job} />
                {getStatusBadge?.(job.status)}
                {isOverdue && (
                  <Badge variant="destructive" className="animate-pulse">Overdue</Badge>
                )}
                {isJobScheduleOverdue && (
                  <Badge className="bg-red-600 text-white border-red-700 font-semibold px-3 py-1">Overdue</Badge>
                )}
                {job.site_qr_required && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                    style={{
                      background: 'rgba(96,165,250,0.12)',
                      color: '#60A5FA',
                      border: '1px solid rgba(96,165,250,0.25)',
                    }}
                    data-testid={`qr-required-badge-${job.id}`}
                  >
                    <QrCode className="w-2.5 h-2.5" />
                    QR required
                  </span>
                )}
              </div>
              {(job.label_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`job-labels-row-${job.id}`}>
                  {(job.label_ids || [])
                    .map((lid) => labelsById?.[lid])
                    .filter(Boolean)
                    .map((l) => <LabelChip key={l.id} label={l} size="sm" />)}
                </div>
              )}
              {job.description && (
                <p className="text-sm text-slate-400 mb-2">{job.description}</p>
              )}
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {job.customer_name && (
              <div className="flex items-center space-x-2 text-slate-300">
                <UserIcon className="w-4 h-4 text-[#1E88E5]" />
                <span className="font-medium">Customer:</span>
                <span>{job.customer_name}</span>
              </div>
            )}
            {job.job_address && (
              <div className="flex items-center space-x-2 text-slate-300">
                <MapPin className="w-4 h-4 text-[#1E88E5]" />
                <span className="font-medium">Location:</span>
                <span className="truncate">{job.job_address}</span>
              </div>
            )}
            {canManageJobs && (
              <div className="text-slate-300">
                {job.is_team_job && job.team_name ? (
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-[#A78BFA]" />
                    <span className="font-medium">Team:</span>
                    <Badge variant="outline" className="bg-[#0F1923] text-purple-700 border-purple-300">
                      {job.team_name}
                    </Badge>
                    {job.crew_names && <span className="text-[11px] text-slate-400" data-testid={`job-ack-count-${job.id}`}>{(job.assignment_acknowledged_by || []).filter((a) => job.crew_names[a.user_id]).length}/{Object.keys(job.crew_names).length} got it</span>}
                    {users.length > 0 && <JobAssignees job={{ ...job, is_team_job: false, crew_names: null }} getUserName={() => ''} canManage users={users} onChanged={onCrewChanged} iconClass="hidden" />}
                  </div>
                ) : (
                  <JobAssignees job={job} getUserName={getUserName} canManage users={users} onChanged={onCrewChanged} />
                )}
              </div>
            )}
            {(job.started_on_behalf || job.completed_on_behalf || job.paused_on_behalf) && (
              <div
                className="flex items-center space-x-2 text-amber-400 text-xs"
                data-testid={`on-behalf-audit-${job.id}`}
              >
                <UserCog className="w-3.5 h-3.5" />
                <span className="font-medium">
                  {job.completed_on_behalf
                    ? `Completed on behalf by ${job.completed_by_name || 'admin'}`
                    : job.paused_on_behalf
                      ? `Paused on behalf by ${job.paused_by_name || 'admin'}`
                      : `Started on behalf by ${job.started_by_name || 'admin'}`}
                </span>
              </div>
            )}
            {timeSince && (
              <div className={`flex items-center space-x-2 ${isOverdue ? 'text-red-700 font-bold' : 'text-slate-300'}`}>
                <Clock className="w-4 h-4" />
                <span className="font-medium">Assigned:</span>
                <span>{timeSince}</span>
              </div>
            )}
            {job.scheduled_start && (
              <div className="flex items-center space-x-2 text-slate-300">
                <Clock className="w-4 h-4 text-[#818CF8]" />
                <span className="font-medium">Scheduled:</span>
                <span>{new Date(job.scheduled_start).toLocaleDateString()}</span>
              </div>
            )}
            {job.scheduled_completion && (
              <div className={`flex items-center space-x-2 ${isJobScheduleOverdue ? 'text-red-700 font-bold' : 'text-slate-300'}`}>
                <Clock className="w-4 h-4 text-[#EF4444]" />
                <span className="font-medium">Due:</span>
                <span>{new Date(job.scheduled_completion).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {/* Per-status action footer rendered inline by the parent */}
          {children}
        </div>
      </CardContent>
    </Card>
  );
}
